<template>
    <div>
        <div v-if="!isLoading" class="fade-in">
            <div class="table-responsive mb-0">
                <table class="table table-hover mb-0">
                    <thead class="bg-light text-muted small">
                        <tr>
                            <th class="text-center text-nowrap">
                                <i v-if="isSequenceLoading" class="mdi mdi-loading mdi-spin text-info mr-1"></i>
                                <div v-else>#</div>
                            </th>
                            <th class="text-nowrap">{{ $t('label.name') }}</th>
                            <th class="text-nowrap">Extension</th>
                            <th class="text-nowrap">{{ $t('label.phone') }}</th>
                            <th class="text-nowrap">DID call</th>
                            <th class="text-nowrap">{{ $t('label.email') }}</th>
                            <th class="text-nowrap">{{ $t('label.action') }}</th>
                        </tr>
                    </thead>
                    <draggable
                        v-model="tableRows"
                        tag="tbody"
                        handle=".item-drag-handle"
                        ghost-class="drag-ghost"
                        chosen-class="drag-chosen"
                        drag-class="drag-active"
                        :animation="300"
                        easing="cubic-bezier(0.25, 1, 0.5, 1)"
                        :disabled="!status.save || isSequenceLoading"
                        @end="updateSequenceRequest"
                    >
                        <tr v-for="(item, index) in tableRows" :key="index">
                            <td class="text-center align-middle text-muted text-nowrap"><i class="mdi mdi-menu-swap font-size-16 cursor-pointer mr-2 item-drag-handle"></i></td>
                            <td class="align-middle"><span class="font-weight-bold text-break">{{ item.user ? (item.user.fullName || [item.user.firstName, item.user.lastName].filter(Boolean).join(' ') || '--') : (item.name || '--') }}</span></td>
                            <td class="align-middle text-nowrap"><span class="badge badge-soft-info font-size-12">{{ item.ext || '--' }}</span></td>
                            <td class="align-middle text-nowrap">{{ item.user ? (item.user.phone ? [item.user.phoneCode, item.user.phone].filter(Boolean).join(' ') : '--') : (item.phoneNumber ? [item.phoneCode, item.phoneNumber].filter(Boolean).join(' ') : '--') }}</td>
                            <td class="align-middle text-nowrap">{{ item.didCall || '--' }}</td>
                            <td class="align-middle text-break">{{ item.user ? (item.user.email || '--') : (item.email || '--') }}</td>
                            <td class="align-middle text-nowrap">
                                <b-button type="button" variant="link" class="text-info p-0 mr-1" :disabled="!status.save || isSequenceLoading" :title="$t('label.updateX', [])" v-b-tooltip.hover="{ boundary: 'window' }" @click="openForm(item)"><i class="mdi mdi-pencil font-size-14"></i></b-button>
                                <b-button type="button" variant="link" class="text-danger p-0" :disabled="!status.save || isSequenceLoading" :title="$t('label.deleteX', [])" v-b-tooltip.hover="{ boundary: 'window' }" @click="deleteConfirmation(item)"><i class="mdi mdi-delete-outline font-size-14"></i></b-button>
                            </td>
                        </tr>
                    </draggable>
                </table>
            </div>
            <div class="p-1 d-flex justify-content-center">
                <b-button type="button" variant="outline-info" size="sm" class="border-info border-dashed font-size-10 font-weight-bolder w-50" :disabled="!status.save || isSequenceLoading" @click="openForm()">
                    <i class="mdi mdi-plus-circle-outline mr-2"></i><small>{{ $t('label.addX', [$t('label.contact')]) }}</small>
                </b-button>
            </div>
        </div>
        <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
            <b-spinner class="m-5" variant="info" role="status" />
        </div>
        <b-modal
            :id="formModalId"
            :title="$t(item.uuid ? 'label.updateX' : 'label.createX', [$t('label.contact')])"
            @hidden="closeForm"
            hide-footer
            hide-header-close
            centered
            size="lg"
            :no-close-on-backdrop="!status.save"
            :no-close-on-esc="!status.save"
        >
            <b-container fluid>
                <PhoneExtItemForm
                    v-if="showForm"
                    :departmentUUID="departmentUUID"
                    :itemUUID="item.uuid"
                    :sequence="item.sequence"
                    @done="closeForm"
                    @reload="getItemList"
                />
            </b-container>
        </b-modal>
        <b-modal :id="deleteModalId" :title="$t('label.deleteX', [$t('label.phone')])" hide-header-close :no-close-on-backdrop="!status.save" :no-close-on-esc="!status.save">
            <p class="my-4">{{ $t('message.deleteConfirmation') }}</p>
            <template #modal-footer>
                <b-button variant="danger" :disabled="!status.save" @click="$bvModal.hide(deleteModalId)">{{ $t('label.cancel') }}</b-button>
                <b-button variant="info" :disabled="!status.save" @click="requestDelete">
                    <span v-if="status.save">{{ $t('label.deleteX', []) }}</span>
                    <b-spinner v-else small />
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import draggable from 'vuedraggable';
import { mapState } from 'vuex';
import { phoneExtItemMethods } from '@/state/api';
import { PHONE_EXT_ITEM_MODULE } from '@/state/api/phoneExtItem.state';
import PhoneExtItemForm from '@/views/pages/admin/phoneExtension/phoneExtensionItem/phoneExtItemForm';

export default {
    props: {
        departmentUUID: { type: String, required: true },
    },
    components: { draggable, PhoneExtItemForm },
    created() {
        this.getItemList();
    },
    data() {
        return {
            isLoading: true,
            isSequenceLoading: false,
            showForm: false,
            item: { uuid: '', sequence: 1 },
        };
    },
    methods: {
        ...phoneExtItemMethods,
        getItemList() {
            this.isLoading = true;
            return this.getAllPhoneExtItem({filter: {phoneExtDepartmentUuid: this.departmentUUID, isActive: 1}})
                .then((result) => {
                    this.isLoading = false;
                    return result;
                });
        },
        updateSequenceRequest(event) {
            if (event.oldIndex == event.newIndex) return;
            this.isSequenceLoading = true;
            let data = {
                phoneExtDepartmentUuid: this.departmentUUID,
                items: this.tableRows.map((item, index) => ({uuid: item.uuid, sequence: index + 1})),
            };
            return this.updatePhoneExtItemSequence({data})
                .then(() => this.getItemList())
                .then(() => { this.isSequenceLoading = false; });
        },
        openForm(item) {
            if (!this.status.save || this.isSequenceLoading) return;
            this.item = item?.uuid ? {uuid: item.uuid, sequence: item.sequence} : {
                uuid: '',
                sequence: Math.max(0, ...this.tableRows.map(row => Number(row.sequence) || 0)) + 1,
            };
            this.showForm = true;
            this.$nextTick(() => this.$bvModal.show(this.formModalId));
        },
        closeForm() {
            this.showForm = false;
            this.item = { uuid: '', sequence: 1 };
            this.$bvModal.hide(this.formModalId);
        },
        deleteConfirmation(item) {
            this.item = {uuid: item.uuid, sequence: item.sequence};
            this.$bvModal.show(this.deleteModalId);
        },
        requestDelete() {
            if (!this.status.save) return;
            let data = {uuid: this.item.uuid, isActive: 0};
            return this.updatePhoneExtItemStatus({data})
                .then((result) => {
                    if (result?.success) {
                        this.$bvModal.hide(this.deleteModalId);
                        return this.getItemList();
                    }
                });
        },
    },
    computed: {
        ...mapState(PHONE_EXT_ITEM_MODULE, ['status']),
        formModalId() {
            return `phone-ext-item-form-${this.departmentUUID}`;
        },
        deleteModalId() {
            return `phone-ext-item-delete-${this.departmentUUID}`;
        },
        tableRows: {
            get() {
                return (this.$store.state.api.phoneExtItem.listByDepartment[this.departmentUUID] || [])
                    .map(item => ({...item}));
            },
            set(value) {
                this.$store.commit(`${PHONE_EXT_ITEM_MODULE}/setPhoneExtItemByDepartment`, {
                    phoneExtDepartmentUuid: this.departmentUUID,
                    data: value.map((item, index) => ({...item, sequence: index + 1})),
                });
            },
        },
    },
};
</script>
<style scoped>
.item-drag-handle {
    cursor: grab;
}
.border-dashed {
    border-style: dashed !important;
}
</style>
